import type { ContentHref } from '@generalTypes/apiTypes';
import type { ValidationErrorWithNode } from '@newStore/validation/validationTypes';
import { useDispatch } from 'react-redux';

import { editNode } from '@newStore/documentUI/documentUIState';
import { selectAppliedNodeConfig } from '@newStore/documentUI/nodeTypeConfigSelectors';
import { isBuildingBlockNodeConfig } from '@nodeTypeConfig/configTypes';
import { useScrollToNode } from '../scrollToNode/useScrollToNode';

import './ValidationErrors.scss';
import { useTypedSelector } from '../../../hooks/useTypedSelector';

const ValidationErrors: React.FC<{
  href: ContentHref;
  validationErrors: Array<ValidationErrorWithNode> | undefined;
  defaultMinimized: boolean;
}> = ({ href, validationErrors, defaultMinimized }) => {
  const dispatch = useDispatch();
  const scrollToNode = useScrollToNode(true);
  const nodeTypeConfig = useTypedSelector((state) => selectAppliedNodeConfig(state, href));

  if (!validationErrors || !(validationErrors?.length > 0)) {
    return null;
  }

  const hasErrorType = (validations: Array<ValidationErrorWithNode>) => {
    return validations.some((v) => v.type === 'ERROR');
  };

  const isReadOnlyNode = isBuildingBlockNodeConfig(nodeTypeConfig) && !!nodeTypeConfig.readOnlyNode;

  const scrollToAndOpenAside = (error: ValidationErrorWithNode) => {
    scrollToNode(error.node.href, error.node.parentHref);
    // only open the aside if the error is on the current node and the node is not read-only
    if (error.node.href === href && !isReadOnlyNode) {
      dispatch(
        editNode({ href, config: { property: error.property, component: error.component } }),
      );
    }
  };

  return (
    <div className={`validation-message-container ${defaultMinimized ? 'minimized' : 'maximized'}`}>
      {validationErrors.length > 1 && (
        <span
          className={`show-more-icon ${hasErrorType(validationErrors) ? 'error' : 'warning'}`}
        >{`+${validationErrors.length - 1}`}</span>
      )}

      {validationErrors.map((error) => (
        <div
          key={error.code}
          className={`error-message type-${error.type.toLowerCase()}`}
          onClick={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
            event.stopPropagation();
            scrollToAndOpenAside(error);
          }}
        >
          <div className="error-message-content">
            <span dangerouslySetInnerHTML={{ __html: error.message }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ValidationErrors;
